import {
  SUPPORTED_MAC_OS_VERSION_NAMES,
  SUPPORTED_WINDOWS_BASE_BUILD_NUMBERS,
} from "../../../../filters/smartFilters/hostSmartFiltersInfo";

// WINDOWS OS functions
// The supported version for each Windows OS are from the following sources:
// Win 11: https://learn.microsoft.com/en-us/windows/release-health/windows11-release-information
// Win 10: https://learn.microsoft.com/en-us/windows/release-health/release-information
export const windowsOsVersionMap = {
  "10.0.26100": "Windows 11",
  "10.0.22631": "Windows 11",
  "10.0.22621": "Windows 11",
  "10.0.19045": "Windows 10",
  "10.0.19044": "Windows 10",
  "10.0.17763": "Windows 10",
  "10.0.14393": "Windows 10",
  "10.0.10240": "Windows 10",
};

// Default minimum build numbers for Windows versions
export const defaultWindowsMinBuildNumbers = {
  "Windows 11": 22621,
};

// Default macOS latest versions with minimum minor versions
export const defaultMacOSSettings = {
  26: { name: "Tahoe", minorVersion: 0, patchVersion: 0, enabled: true },
  16: { name: "Tahoe", minorVersion: 0, patchVersion: 0, enabled: true },
  15: { name: "Sequoia", minorVersion: 4, patchVersion: 0, enabled: true },
  14: { name: "Sonoma", minorVersion: 7, patchVersion: 3, enabled: true },
};

// These versions are specific prod-3 request. They are shown when the toggle is on
export const windowsLatestVersion = {
  "10.0.26100": "Windows 11",
  "10.0.22631": "Windows 11",
};

// Helper function to determine Windows OS name from version number
export const getWindowsOSNameFromVersion = (version) => {
  if (windowsOsVersionMap[version]) {
    return windowsOsVersionMap[version];
  }

  // Try to determine OS from version pattern if not in the map
  if (version.startsWith("10.0")) {
    const buildNumber = parseInt(version.split(".")[2], 10);
    if (buildNumber >= 22000) {
      return "Windows 11";
    } else if (buildNumber >= 10240) {
      return "Windows 10";
    }
  } else if (version.startsWith("6.3") || version.startsWith("6.2")) {
    return "Windows 8";
  } else if (version.startsWith("6.1")) {
    return "Windows 7";
  }

  return `Windows (${version})`;
};

// Helper function to get the build number from a Windows version string
export const getBuildNumber = (version) => {
  if (!version) return 0;
  const parts = version.split(".");
  return parts.length >= 3 ? parseInt(parts[2], 10) : 0;
};

export const createMapFullWindowsOsToVersion = (
  version,
  fullOSName,
  fullOSToNameWindows
) => {
  if (!version) return "";
  if (!fullOSToNameWindows[fullOSName]) {
    fullOSToNameWindows[fullOSName] = [];
  }
  fullOSToNameWindows[fullOSName].push(version);
};

export const getWindowsOSName = (version, latestToggleOn, customSettings) => {
  if (!version) return "Windows";
  //get osVersion xx.x.xxxx
  const osVersionNumber = version.split(".");
  if (osVersionNumber.length < 3) {
    return "Windows";
  }
  const osVersion =
    osVersionNumber[0] + "." + osVersionNumber[1] + "." + osVersionNumber[2];
  const buildNumber = getBuildNumber(version);
  const osName = getWindowsOSNameFromVersion(osVersion);

  if (latestToggleOn) {
    // If custom settings are provided and the version exists, use custom settings
    if (customSettings?.windows && osVersion in customSettings.windows) {
      return customSettings.windows[osVersion] ? osName : "";
    }
    // Otherwise fall back to default settings
    if (windowsLatestVersion[osVersion]) {
      return windowsLatestVersion[osVersion];
    }

    // For Windows 10 versions, include them if their build number is >= the default minimum
    if (osName === "Windows 10" || osName === "Windows 11") {
      // Get the minimum build number for this OS version
      const minBuildNumber =
        defaultWindowsMinBuildNumbers[osName] ||
        (osName === "Windows 11" ? 22000 : 19044);

      if (buildNumber >= minBuildNumber) {
        return osName;
      }
    }
  }
  return getWindowsOSNameFromVersion(osVersion);
};

export const isWindowsVersionSupported = (
  version,
  latestToggleOn,
  customSettings
) => {
  if (!version) return true;
  const osVersionNumber = version.split(".");
  const osVersion =
    osVersionNumber[0] + "." + osVersionNumber[1] + "." + osVersionNumber[2];
  const buildNumber = getBuildNumber(version);
  const osName = getWindowsOSNameFromVersion(osVersion);

  if (latestToggleOn) {
    // If custom settings are provided and the version exists, use custom settings
    if (customSettings?.windows && osVersion in customSettings.windows) {
      return customSettings.windows[osVersion];
    }
    // Otherwise fall back to default settings
    if (windowsLatestVersion[osVersion]) {
      return true;
    }

    // For Windows 10 and 11 versions, check against the minimum build number
    if (osName === "Windows 10" || osName === "Windows 11") {
      // Get the minimum build number for this OS version
      const minBuildNumber =
        defaultWindowsMinBuildNumbers[osName] ||
        (osName === "Windows 11" ? 22000 : 19044);

      return buildNumber >= minBuildNumber;
    }

    return false;
  }
  if (
    SUPPORTED_WINDOWS_BASE_BUILD_NUMBERS.some((baseBuildNumber) =>
      version.includes(baseBuildNumber.toLowerCase())
    )
  )
    return true;
  if (version.toLowerCase().includes("server")) return true;
  if (version === "Windows 11") return true;
  return false;
};

// MACOS Functions

// Map of macOS supported version to name
export const macOSNameMap = {
  26: "Tahoe 26",
  16: "Tahoe 16",
  15: "Sequoia 15",
  14: "Sonoma 14",
  13: "Ventura 13",
};

// These versions are specific prod-3 request. They are shown when the toggle is on
export const macOSLatestVersion = {
  26: "Tahoe",
  16: "Tahoe",
  15: "Sequoia",
  14: "Sonoma",
};

export const getMacOSName = (version, latestToggleOn, customSettings) => {
  if (!version) return "MacOS";
  const major = version.split(".")[0]; // Extracts "15" from "15.0.1"
  if (latestToggleOn) {
    // If custom settings are provided
    if (customSettings?.mac && customSettings.mac[major]) {
      return customSettings.mac[major].enabled ? macOSNameMap[major] : "";
    }
    // Otherwise fall back to default behavior
    if (macOSLatestVersion[major]) {
      return macOSLatestVersion[major];
    }
  } else {
    // Direct match for major version
    if (macOSNameMap[major]) {
      return macOSNameMap[major];
    }
  }
  return "";
};

export const createMapFullMacOsToVersion = (
  version,
  fullOSName,
  fullOSToNameMac
) => {
  if (!version) return "";
  if (!fullOSToNameMac[fullOSName]) {
    fullOSToNameMac[fullOSName] = [];
  }
  fullOSToNameMac[fullOSName].push(version);
};

export const isMacOSVersionSupported = (
  version,
  latestToggleOn,
  customSettings
) => {
  if (!version) return true;
  if (latestToggleOn) {
    return isMacOSVersionLatest(version, customSettings);
  }
  if (
    SUPPORTED_MAC_OS_VERSION_NAMES.some((versionName) =>
      version.toLowerCase().includes(versionName.toLowerCase())
    )
  ) {
    return true;
  }
  if (!isNaN(version.charAt(0))) {
    const major = version.split(".")[0];
    if (Number(major) >= 13) return true;
  }
  return false;
};

// Build data for the chart function

// check if the version is considered latest based on custom settings or defaults
export const isMacOSVersionLatest = (version, customSettings) => {
  if (!version) return true;
  if (!isNaN(version.charAt(0))) {
    const parts = version.split(".").map(Number);
    const major = parts[0];
    const minor = parts.length > 1 ? parts[1] : 0;
    const patch = parts.length > 2 ? parts[2] : 0;

    // Use custom settings if provided
    if (customSettings?.mac && customSettings.mac[major]) {
      const settings = customSettings.mac[major];
      // If this major version is disabled, it's not considered latest
      if (!settings.enabled) return false;

      // Compare version numbers (major.minor.patch)
      if (major > Number(major)) return true;
      if (major === Number(major)) {
        if (minor > settings.minorVersion) return true;
        if (minor === settings.minorVersion) {
          return patch >= (settings.patchVersion || 0);
        }
      }
      return false;
    }
    if (major === 26 || major === 16) {
      return true;
    }

    // Fall back to default settings
    const sequoiaLatestMajor = 15;
    const sequoiaLatestMinor = 4;
    const sequoiaLatestPatch = 0;

    const sonomaLatestMajor = 14;
    const sonomaLatestMinor = 7;
    const sonomaLatestPatch = 3;

    // Sequoia
    if (major > sequoiaLatestMajor) return true;
    if (major === sequoiaLatestMajor) {
      if (minor > sequoiaLatestMinor) return true;
      if (minor === sequoiaLatestMinor) {
        return patch >= sequoiaLatestPatch;
      }
    }
    // Sonoma
    if (major === sonomaLatestMajor) {
      if (minor > sonomaLatestMinor) return true;
      if (minor === sonomaLatestMinor) {
        return patch >= sonomaLatestPatch;
      }
    }
    return false;
  }
  return false;
};

// return osVersion if it is supported
export const processEntities = (
  entities,
  platform,
  getOSName,
  isVersionSupported,
  createMapFullOsToVersion,
  latestToggleOn,
  fullOSToName,
  customSettings
) => {
  const olderVersionName = "Older Versions";
  const versionCounts = { [olderVersionName]: 0 };
  entities
    .filter((entity) => entity.platform === platform)
    ?.forEach((entity) => {
      // if os_version not exist skip
      if (!entity.os_version) return;
      const isDeprecated = !isVersionSupported(
        entity.os_version,
        latestToggleOn,
        customSettings
      );

      if (isDeprecated) {
        createMapFullOsToVersion(
          entity.os_version,
          "Older Versions",
          fullOSToName
        );
        versionCounts[olderVersionName]++;
      } else {
        let osVersion = getOSName(
          entity.os_version,
          latestToggleOn,
          customSettings
        );
        createMapFullOsToVersion(entity.os_version, osVersion, fullOSToName);
        if (!osVersion) {
          osVersion = entity.os_version;
        } else {
          osVersion += latestToggleOn ? " (latest)" : " (Supported)";
        }
        versionCounts[osVersion] = (versionCounts[osVersion] || 0) + 1;
      }
    });
  return versionCounts;
};

export const createDataset = (versionCounts, platform, colors) => {
  return Object.keys(versionCounts)?.map((osVersion) => {
    const olderVersion = osVersion === "Older Versions";
    return {
      platform,
      version: osVersion.replace(`${platform} `, ""),
      os: osVersion,
      olderVersion,
      color: olderVersion ? colors.red : colors.light_green,
      count: versionCounts[osVersion] === 0 ? 0.02 : versionCounts[osVersion],
    };
  });
};

export const sortDataset = (dataset) => {
  dataset.sort((a, b) => {
    if (a.os.includes("Older Versions")) return 1;
    if (b.os.includes("Older Versions")) return -1;
    return a.platform.localeCompare(b.platform);
  });
};

// Helper function to initialize default version settings
export const getDefaultVersionSettings = () => {
  // Convert Windows latest versions to a map of {version: boolean}
  const windowsSettings = Object.keys(windowsOsVersionMap).reduce(
    (acc, version) => {
      // By default, enable Windows 10 versions in aggregate mode
      if (windowsOsVersionMap[version] === "Windows 10") {
        acc[version] = true;
      } else {
        acc[version] = !!windowsLatestVersion[version];
      }
      return acc;
    },
    {}
  );

  return {
    windows: windowsSettings,
    mac: { ...defaultMacOSSettings },
  };
};
